import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Copy, Check, Share2, Trophy, TrendingUp, AlertTriangle, Lightbulb, DollarSign, Users, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLeaderboard } from "@/hooks/use-leaderboard-mutations";
import { useEntries } from "@/hooks/use-entries";
import { useAuth } from "@/contexts/auth-context";

const ManagementReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const { effectiveVenueId } = useAuth();
  const { data: leaderboard, isLoading: leaderboardLoading } = useLeaderboard(effectiveVenueId, id ?? null);
  const { data: entries = [] } = useEntries(effectiveVenueId, id ?? null);

  const top5 = entries.slice(0, 5);
  const winner = top5[0];

  const shareUrl = `${window.location.origin}/app/campaigns/${id}/management-report`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalSold = top5.reduce((s, m) => s + m.membershipsSold, 0);
  const totalValue = top5.reduce((s, m) => s + m.valueSold, 0);
  const avgSold = top5.length > 0 ? Math.round(totalSold / top5.length) : 0;

  if (leaderboardLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!leaderboard) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Campaign not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button onClick={() => navigate("/app")} className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to campaigns
          </button>
          <h1 className="text-2xl font-bold">Management Report</h1>
          <p className="text-muted-foreground">{leaderboard.name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCopy}>
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Copied!" : "Copy Link"}
          </Button>
          <Button variant="outline" onClick={handleCopy}>
            <Share2 className="mr-2 h-4 w-4" /> Share
          </Button>
        </div>
      </div>

      {/* KPI summary */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total Score", value: totalSold.toLocaleString(), icon: <DollarSign className="h-5 w-5" />, color: "text-accent" },
          { label: "Avg Score", value: avgSold.toLocaleString(), icon: <TrendingUp className="h-5 w-5" />, color: "text-accent" },
          { label: "Total Value", value: `$${totalValue.toLocaleString()}`, icon: <Target className="h-5 w-5" />, color: "text-accent" },
          { label: "Staff on Board", value: `${entries.length}`, icon: <Users className="h-5 w-5" />, color: "text-primary" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl border bg-card p-5">
            <div className={`mb-2 ${kpi.color}`}>{kpi.icon}</div>
            <p className="text-2xl font-bold">{kpi.value}</p>
            <p className="text-sm text-muted-foreground">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Winner spotlight */}
      {winner && (
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-accent" /> Campaign Leader
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10 text-2xl font-bold text-accent border-2 border-accent">
              {winner.name.charAt(0)}
            </div>
            <div>
              <p className="text-xl font-bold">{winner.name}</p>
              <p className="text-muted-foreground">{winner.membershipsSold} sold · ${winner.valueSold.toLocaleString()} revenue</p>
            </div>
          </div>
        </div>
      )}

      {/* Final leaderboard */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Final Leaderboard</h2>
        <div className="space-y-2">
          {top5.map((s, i) => (
            <div key={s.id} className={`flex items-center gap-3 rounded-lg px-4 py-3 ${i === 0 ? "bg-accent/10 border border-accent/20" : "bg-muted/50"}`}>
              <span className={`w-8 text-center text-lg font-extrabold ${i === 0 ? "text-accent" : i < 3 ? "text-foreground" : "text-muted-foreground"}`}>
                {i + 1}
              </span>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-bold">
                {s.name.charAt(0)}
              </div>
              <span className="flex-1 font-medium">{s.name}</span>
              <span className="text-sm font-bold">{s.membershipsSold} sold</span>
              <span className="text-sm text-muted-foreground">${s.valueSold.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Insights & Recommendations */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-accent" /> What Went Well
          </h2>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
              <span>Top performer {winner?.name ?? "—"} sold {winner?.membershipsSold ?? 0} units, leading the team.</span>
            </li>
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
              <span>Campaign generated ${totalValue.toLocaleString()} in total tracked value across {entries.length} staff members.</span>
            </li>
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
              <span>All {entries.length} staff on the leaderboard, showing strong engagement with the format.</span>
            </li>
          </ul>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-primary" /> Areas for Improvement
          </h2>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Bottom 50% of staff averaged only {Math.round(totalSold / top5.length * 0.6)} sales — consider targeted coaching or pairing with top performers.</span>
            </li>
            <li className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Weekend shifts showed lower conversion rates. Try scheduling your highest performers on peak traffic days.</span>
            </li>
            <li className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Consider adding mid-campaign rewards (e.g., daily prizes) to sustain momentum through the final week.</span>
            </li>
            <li className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Test a shorter campaign window (2 weeks vs. 4 weeks) to maintain urgency and prevent fatigue.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Share CTA */}
      <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-6 text-center">
        <h3 className="text-lg font-semibold">Share this report with your team</h3>
        <p className="mt-1 text-sm text-muted-foreground">Copy the link below to share this management report with stakeholders.</p>
        <div className="mt-4 flex items-center gap-2 justify-center">
          <code className="rounded-lg bg-muted px-4 py-2 text-sm text-muted-foreground truncate max-w-md">{shareUrl}</code>
          <Button size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManagementReportPage;
